/**
 * TokenSettings — Install Martha across MCP clients + manage tokens.
 * Leads with one-click install cards, auto-creates tokens behind the scenes.
 * Token CRUD is collapsed below for advanced users.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Key,
  Plus,
  Copy,
  Check,
  Trash2,
  Clock,
  AlertTriangle,
  Shield,
  ChevronRight,
  Terminal,
  Monitor,
  MousePointer2,
  Zap,
  Users,
  Eye,
} from 'lucide-react';
import { authApi, type ApiKey } from '../../api/auth';
import { useAuth } from '../../contexts/AuthContext';
import { useToolManifest } from '../../api/mcp';
import { FALLBACK_TOOL_GROUPS, manifestToToolGroups } from '../../constants/mcp-tools';
import {
  ClientInstallCard,
  buildFullConfig,
  buildClaudeCodeCommand,
  buildDesktopConfig,
  buildDesktopDeepLink,
  buildConnectorUrl,
  buildClaudeCodePluginCommand,
} from './install-cards';
import type { ClientDef } from './install-cards';

// ─── Copy Button ────────────────────────────────────────────────
function CopyBtn({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all
        text-muted hover:text-text hover:bg-surface-subtle active:scale-95"
      title="Copy to clipboard"
    >
      {copied ? (
        <>
          <Check className="w-3.5 h-3.5 text-green-500" />
          {label ? <span className="text-green-600">Copied</span> : null}
        </>
      ) : (
        <>
          <Copy className="w-3.5 h-3.5" />
          {label ? <span>{label}</span> : null}
        </>
      )}
    </button>
  );
}

// A 401 FRESH_SIGN_IN_REQUIRED means the session is too old to manage API keys.
// Tested by status and code (not instanceof) so it holds across module boundaries.
function isFreshSignInRequired(err: unknown): boolean {
  const e = err as { status?: unknown; code?: unknown } | null;
  return !!e && e.status === 401 && e.code === 'FRESH_SIGN_IN_REQUIRED';
}

// ─── Time formatting ────────────────────────────────────────────
function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// ─── Client Definitions ─────────────────────────────────────────
const CLIENT_DEFS: ClientDef[] = [
  {
    id: 'claude-code',
    name: 'Claude Code',
    description: 'Full Martha plugin — skills, commands + tools',
    icon: Terminal,
    // With a token (fresh or pasted) include the MCP setup keyed to it;
    // without one, the plugin alone (its bundled connector uses OAuth).
    buildInstruction: (token) =>
      token
        ? `# Full plugin (skills + commands):\n${buildClaudeCodePluginCommand()}\n\n# MCP tools with your key:\n${buildClaudeCodeCommand(token)}`
        : buildClaudeCodePluginCommand(),
    hint: 'Restart Claude Code, then /martha:search, /martha:comms, etc.',
  },
  {
    id: 'claude-desktop',
    name: 'Claude Desktop',
    description: 'Anthropic desktop app — install via mcp-remote bridge',
    icon: Monitor,
    buildInstruction: (token) => buildDesktopConfig(token),
    buildDeepLink: (token) => buildDesktopDeepLink(token),
    hint: 'Requires Node.js. mcp-remote bridges Claude Desktop to Martha\'s HTTP endpoint.',
    configPaths: [
      { label: 'macOS', path: '~/Library/Application Support/Claude/claude_desktop_config.json' },
      { label: 'Windows', path: '%APPDATA%\\Claude\\claude_desktop_config.json' },
    ],
  },
  {
    id: 'cowork',
    name: 'Cowork',
    description: 'Add Martha as a remote connector (OAuth)',
    icon: Users,
    buildInstruction: () => buildConnectorUrl(),
    hint: 'In Cowork → add a custom connector and paste this URL. It signs in with your Martha account (OAuth) — no key needed.',
    tokenless: true,
  },
  {
    id: 'cursor',
    name: 'Cursor',
    description: 'Add to .cursor/mcp.json in your project',
    icon: MousePointer2,
    buildInstruction: (token) => buildFullConfig(token),
    hint: 'Or add globally in Cursor Settings > MCP Servers.',
    configPaths: [
      { label: 'Project', path: '.cursor/mcp.json' },
    ],
  },
  {
    id: 'zed',
    name: 'Zed',
    description: 'Add to Zed settings.json context_servers',
    icon: Zap,
    buildInstruction: (token) => buildFullConfig(token),
    configPaths: [
      { label: 'Config', path: '~/.config/zed/settings.json' },
    ],
  },
];

// ─── Main Component ─────────────────────────────────────────────
export const TokenSettings: React.FC = () => {
  const { user, logout } = useAuth();
  const { data: manifest } = useToolManifest();
  const toolGroups = manifest ? manifestToToolGroups(manifest) : FALLBACK_TOOL_GROUPS;

  // Token list state
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Set when list, create or reveal was refused because the sign-in is too old
  const [freshSignIn, setFreshSignIn] = useState(false);

  // Create form state (for manual token management)
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Revoke confirmation
  const [revoking, setRevoking] = useState<string | null>(null);
  // keyId → revealed full key (fetched on demand from /api-keys/:id/reveal)
  const [revealed, setRevealed] = useState<Record<string, string>>({});

  const handleReveal = useCallback(async (keyId: string) => {
    if (revealed[keyId]) {
      setRevealed((r) => { const n = { ...r }; delete n[keyId]; return n; }); // toggle off
      return;
    }
    try {
      const res = await authApi.revealApiKey(keyId);
      setRevealed((r) => ({ ...r, [keyId]: res.key }));
    } catch (err: unknown) {
      if (isFreshSignInRequired(err)) {
        setFreshSignIn(true);
      }
      // otherwise: pre-feature key or decrypt failure — leave truncated display
    }
  }, [revealed]);

  // One-time reveal of a freshly created key — the API returns the plaintext
  // exactly once (only its SHA-256 hash is stored), so we must show it for copy.
  const [newKey, setNewKey] = useState<string | null>(null);
  const [newKeyCopied, setNewKeyCopied] = useState(false);

  // Tool groups expansion
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  // Fetch keys
  const fetchKeys = useCallback(async () => {
    try {
      setError(null);
      setFreshSignIn(false);
      const res = await authApi.listApiKeys(100);
      setKeys(res.data);
    } catch (err: unknown) {
      if (isFreshSignInRequired(err)) {
        setFreshSignIn(true);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load API keys');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  // Create key (manual)
  const handleCreate = useCallback(async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const created = await authApi.createApiKey(newKeyName.trim());
      setNewKey(created.key);   // reveal once — the plaintext is never recoverable after this
      setNewKeyCopied(false);
      setNewKeyName('');
      setShowCreateForm(false);
      await fetchKeys();
    } catch (err: unknown) {
      if (isFreshSignInRequired(err)) {
        setFreshSignIn(true);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create key');
      }
    } finally {
      setCreating(false);
    }
  }, [newKeyName, fetchKeys]);

  // Revoke key
  const handleRevoke = useCallback(async (keyId: string) => {
    setError(null);
    try {
      await authApi.revokeApiKey(keyId);
      setRevoking(null);
      await fetchKeys();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to revoke key');
    }
  }, [fetchKeys]);

  // Derived
  const activeKeys = keys.filter((k) => !k.revokedAt);
  const revokedKeys = keys.filter((k) => k.revokedAt);

  return (
    <div className="space-y-8">
      {/* ── Section 1: Manage Tokens ───────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-1">
          <div>
            <h2 className="text-sm font-semibold text-text">API Keys</h2>
            <p className="text-xs text-muted mt-0.5">
              Personal access tokens for MCP agent authentication.
              {user ? ` Tied to ${user.email}.` : ''}
            </p>
          </div>
          <button
            onClick={() => { setShowCreateForm(true); }}
            disabled={activeKeys.length >= 10}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
              bg-neutral-900 text-white hover:bg-neutral-800 transition-colors active:scale-[0.97]
              disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" />
            Create Token
          </button>
        </div>

        {/* Capacity warning */}
        {activeKeys.length >= 8 && (
          <div className="flex items-center gap-2 p-3 mt-3 rounded-lg bg-amber-50 border border-amber-200">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <p className="text-xs text-amber-800">
              {activeKeys.length}/10 tokens used.{' '}
              {activeKeys.length >= 10
                ? 'Revoke an unused token before creating new ones.'
                : 'Approaching the 10-token limit.'}
            </p>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-2 p-3 mt-3 rounded-lg bg-red-50 border border-red-200">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-xs text-red-700">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-xs text-red-500 hover:text-red-700">
              Dismiss
            </button>
          </div>
        )}

        {/* Session too old to create, list or reveal keys */}
        {freshSignIn && (
          <div className="flex items-center gap-2 p-3 mt-3 rounded-lg bg-red-50 border border-red-200">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-xs text-red-700">Sign in again to manage API keys</p>
            <button
              onClick={() => { void logout(); }}
              className="ml-auto px-2.5 py-1 rounded-md text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
            >
              Sign in again
            </button>
          </div>
        )}

        {/* One-time key reveal — plaintext is shown ONCE and never again */}
        {newKey && (
          <div className="mt-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
            <div className="flex items-start gap-2">
              <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-emerald-800">
                  Token created — copy it now. For security it won&apos;t be shown again.
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <code className="flex-1 px-2 py-1.5 rounded bg-white border border-emerald-200 text-[11px] font-mono text-text break-all select-all">
                    {newKey}
                  </code>
                  <button
                    onClick={() => {
                      void navigator.clipboard.writeText(newKey ?? '');
                      setNewKeyCopied(true);
                      setTimeout(() => setNewKeyCopied(false), 2000);
                    }}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium
                      bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex-shrink-0"
                  >
                    {newKeyCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {newKeyCopied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
              <button
                onClick={() => setNewKey(null)}
                className="text-xs text-emerald-600 hover:text-emerald-800 flex-shrink-0"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {showCreateForm && (
          <div className="mt-3 p-4 rounded-lg border border-line bg-white">
            <label className="block text-xs font-medium text-text mb-2">Token name</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                placeholder="e.g. CI Pipeline, Zapier"
                className="flex-1 px-3 py-2 text-sm border border-line rounded-lg
                  bg-white text-text placeholder-neutral-400
                  focus:outline-none focus:ring-1 focus:ring-neutral-300 focus:border-transparent"
                autoFocus
                maxLength={255}
              />
              <button
                onClick={handleCreate}
                disabled={creating || !newKeyName.trim()}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-colors
                  bg-neutral-900 text-white hover:bg-neutral-800
                  disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
              <button
                onClick={() => { setShowCreateForm(false); setNewKeyName(''); }}
                className="px-3 py-2 rounded-lg text-xs font-medium text-muted hover:text-text hover:bg-surface-subtle"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-5 h-5 border-2 border-line-strong border-t-neutral-600 rounded-full animate-spin" />
          </div>
        )}

        {!loading && activeKeys.length === 0 && (
          <div className="text-center py-8 mt-3 border border-dashed border-line rounded-lg">
            <Key className="w-6 h-6 text-subtle mx-auto mb-2" />
            <p className="text-xs text-subtle">No tokens yet. Use the Install cards below or create one manually.</p>
          </div>
        )}

        {!loading && activeKeys.length > 0 && (
          <div className="mt-3 border border-line rounded-lg overflow-hidden divide-y divide-neutral-100">
            {activeKeys.map((key) => (
              <div key={key.id} className="flex items-center gap-3 px-4 py-3 hover:bg-surface-muted/50 transition-colors">
                <Key className="w-4 h-4 text-subtle flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-text truncate">{key.name}</span>
                    <code className="text-[11px] font-mono text-subtle bg-surface-subtle px-1.5 py-0.5 rounded select-all">
                      {revealed[key.id] ?? `mk_${key.prefix}_...`}
                    </code>
                    {key.revealable && (
                      <button
                        onClick={() => handleReveal(key.id)}
                        className="p-1 rounded-md text-subtle hover:text-text hover:bg-surface-subtle transition-colors"
                        title={revealed[key.id] ? 'Hide key' : 'Show key'}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {revealed[key.id] && <CopyBtn text={revealed[key.id]} />}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-[11px] text-subtle">Created {timeAgo(key.createdAt)}</span>
                    {key.lastUsedAt && (
                      <span className="text-[11px] text-subtle flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Last used {timeAgo(key.lastUsedAt)}
                      </span>
                    )}
                    {!key.lastUsedAt && (
                      <span className="text-[11px] text-subtle">Never used</span>
                    )}
                  </div>
                </div>
                {revoking === key.id ? (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => handleRevoke(key.id)}
                      className="px-2.5 py-1 rounded-md text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setRevoking(null)}
                      className="px-2.5 py-1 rounded-md text-xs font-medium text-muted hover:text-text hover:bg-surface-subtle"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setRevoking(key.id)}
                    className="p-1.5 rounded-md text-subtle hover:text-red-600 hover:bg-red-50 transition-colors"
                    title="Revoke token"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && revokedKeys.length > 0 && (
          <details className="mt-3">
            <summary className="text-xs text-subtle cursor-pointer hover:text-muted select-none">
              {revokedKeys.length} revoked {revokedKeys.length === 1 ? 'token' : 'tokens'}
            </summary>
            <div className="mt-2 border border-line rounded-lg overflow-hidden divide-y divide-neutral-50">
              {revokedKeys.map((key) => (
                <div key={key.id} className="flex items-center gap-3 px-4 py-2.5 opacity-50">
                  <Key className="w-3.5 h-3.5 text-subtle flex-shrink-0" />
                  <span className="text-xs text-subtle line-through truncate">{key.name}</span>
                  <code className="text-[10px] font-mono text-subtle bg-surface-muted px-1 py-0.5 rounded">
                    mk_{key.prefix}_...
                  </code>
                  <span className="text-[10px] text-subtle ml-auto">
                    Revoked {key.revokedAt ? timeAgo(key.revokedAt) : ''}
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}
      </section>

      {/* ── Section 2: Install Martha ──────────────────── */}
      <section>
        <h2 className="text-sm font-semibold text-text mb-1">Install Martha</h2>
        <p className="text-xs text-muted mb-5">
          Connect Martha to your AI coding tool. A token is created automatically when you click Install.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {CLIENT_DEFS.map((client) => (
            <ClientInstallCard
              key={client.id}
              client={client}
              keys={keys}
              activeKeyCount={activeKeys.length}
              onKeysChanged={fetchKeys}
            />
          ))}
        </div>
      </section>

      {/* ── Section 3: Available Tools ─────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-semibold text-text mb-1">Available Tools</h2>
            <p className="text-xs text-muted">
              {toolGroups.reduce((sum, g) => sum + g.tools.length, 0)} tools across {toolGroups.length} categories
            </p>
          </div>
        </div>
        <div className="border border-line rounded-lg overflow-hidden">
          {toolGroups.map((group) => {
            const isExpanded = expandedGroup === group.label;
            const GroupIcon = group.icon;
            return (
              <div key={group.label} className="border-b border-line last:border-b-0">
                <button
                  onClick={() => setExpandedGroup(isExpanded ? null : group.label)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-muted transition-colors"
                >
                  <GroupIcon className="w-4 h-4 text-muted flex-shrink-0" />
                  <span className="text-sm font-medium text-text flex-1">{group.label}</span>
                  <span className="text-xs text-subtle tabular-nums">{group.tools.length}</span>
                  <ChevronRight
                    className={`w-4 h-4 text-subtle transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  />
                </button>
                {isExpanded && (
                  <div className="border-t border-line px-4 py-2 bg-surface-muted">
                    {group.tools.map((tool) => (
                      <div key={tool.name} className="flex items-baseline gap-3 py-1.5">
                        <code className="font-mono text-xs text-text flex-shrink-0">{tool.name}</code>
                        <span className="text-xs text-subtle">{tool.desc}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Section 4: Security Info ──────────────────── */}
      <section>
        <div className="p-4 rounded-lg bg-surface-muted border border-line">
          <div className="flex items-start gap-3">
            <Shield className="w-4 h-4 text-subtle mt-0.5 flex-shrink-0" />
            <div className="text-xs text-muted space-y-1.5">
              <p className="font-medium text-text">Token security</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Tokens are universal — valid across all workspaces and projects</li>
                <li>Hashed with SHA-256 before storage — we never store the plaintext</li>
                <li>Revoked tokens are rejected immediately on the next request</li>
                <li>Maximum 10 active tokens per user</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
